import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { editListing, editProperty } from "@/modules/property-tracks/api";
import { ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { updateListingPriceV2 } from "@/modules/listing-dashboard/api/index-v2";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { editListingSchema, type EditListingFormValues } from "./schema";
import { PriceFields } from "./price-fields";
import { UnitDetailsFields } from "./unit-details-fields";
import type { EditListingMutationContext, ListingDataForEdit } from "./types";

interface EditListingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  listing: ListingDataForEdit;
  context: EditListingMutationContext;
  isPersonalLead: boolean;
}

// Coerce BE nulls → undefined so zod's `.optional()` validators don't fail
// (zod's optional allows undefined, not null).
function nu<T>(v: T | null | undefined): T | undefined {
  return v === null ? undefined : v;
}

const buildDefaultValues = (
  listing: ListingDataForEdit
): EditListingFormValues => ({
  actualPrice: listing.unitPrice ?? 0,
  downpayment: listing.downpayment ?? 0,
  remainingInstallments: listing.remainingInstallments ?? 0,
  maintenance: nu(listing.maintenance),
  numberOfBedrooms: nu(listing.numberOfBedrooms),
  numberOfBathrooms: nu(listing.numberOfBathrooms),
  unitArea: nu(listing.unitArea),
  floorNumber: nu(listing.floorNumber),
  finishing: nu(listing.finishing) as EditListingFormValues["finishing"],
});

const LISTING_QUERY_KEYS = [
  ["getListings"],
  ["getLeadListings"],
  ["getLeadTracks"],
  ["listings"],
  ["track"],
  ["getTracksForJourney"],
] as const;

// Cancel any in-flight refetches first so a slow GET that started before the
// PATCH can't overwrite the freshly-invalidated cache. Then refetch. No
// setTimeout — the wait was masking the race, not preventing it.
const refreshListingCaches = async () => {
  await Promise.all(
    LISTING_QUERY_KEYS.map((key) =>
      queryClient.cancelQueries({ queryKey: [...key] })
    )
  );
  LISTING_QUERY_KEYS.forEach((key) =>
    queryClient.invalidateQueries({ queryKey: [...key] })
  );
};

export const EditListingModal = ({
  open,
  setOpen,
  listing,
  context,
  isPersonalLead,
}: EditListingModalProps) => {
  const { isMobile } = useScreenSize();

  const baselineValues = useMemo(
    () => buildDefaultValues(listing),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      listing.unitPrice,
      listing.downpayment,
      listing.remainingInstallments,
      listing.maintenance,
      listing.numberOfBedrooms,
      listing.numberOfBathrooms,
      listing.unitArea,
      listing.floorNumber,
      listing.finishing,
    ]
  );

  // `values` keeps the form synced with the latest persisted listing.
  // Whenever baselineValues changes (e.g., after a refetch following submit),
  // RHF re-seeds the form. This avoids the open/close timing pitfalls of
  // manually calling form.reset in a useEffect.
  const form = useForm<EditListingFormValues>({
    resolver: zodResolver(editListingSchema),
    values: baselineValues,
    mode: "onSubmit",
    resetOptions: { keepDirtyValues: false, keepErrors: false },
  });

  const mutation = useMutation(
    async (data: EditListingFormValues) => {
      const priceChanged =
        data.actualPrice !== (listing.unitPrice ?? 0) ||
        data.downpayment !== (listing.downpayment ?? 0) ||
        data.remainingInstallments !== (listing.remainingInstallments ?? 0) ||
        (data.maintenance ?? 0) !== (listing.maintenance ?? 0);

      const propertyChanged =
        isPersonalLead &&
        (data.numberOfBedrooms !== listing.numberOfBedrooms ||
          data.numberOfBathrooms !== listing.numberOfBathrooms ||
          data.unitArea !== listing.unitArea ||
          data.floorNumber !== listing.floorNumber ||
          data.finishing !== listing.finishing);

      const promises: Promise<unknown>[] = [];

      if (priceChanged) {
        const hasInstallments =
          (data.downpayment ?? 0) > 0 || (data.remainingInstallments ?? 0) > 0;
        const paymentType = hasInstallments ? "installments" : "cash";
        const updateListingRequest =
          paymentType === "cash"
            ? {
                paymentType: "cash" as const,
                priceRole: "listing" as const,
                askingPrice: data.actualPrice,
                price: data.actualPrice,
                // Send 0 when the user cleared the field (RHF stores undefined).
                // Otherwise undefined would be dropped by JSON.stringify and the
                // backend would keep the old value, contradicting the user intent.
                maintenance: data.maintenance ?? 0,
              }
            : {
                paymentType: "installments" as const,
                priceRole: "listing" as const,
                price: data.actualPrice,
                downPayment: data.downpayment,
                remainingInstallments: data.remainingInstallments,
                // Send 0 when the user cleared the field (RHF stores undefined).
                // Otherwise undefined would be dropped by JSON.stringify and the
                // backend would keep the old value, contradicting the user intent.
                maintenance: data.maintenance ?? 0,
              };
        promises.push(
          updateListingPriceV2({
            updateListingRequest,
            propertyUid: context.listingId,
            trackUid: context.trackUid,
          })
        );
      }

      if (propertyChanged) {
        const propertyPayload = {
          numberOfBedrooms: data.numberOfBedrooms,
          numberOfBathrooms: data.numberOfBathrooms,
          unitArea: data.unitArea,
          floorNumber: data.floorNumber,
          finishing: data.finishing,
        };
        if (context.listingUid) {
          promises.push(
            editListing(context.listingUid, {
              listingType: "resale" as never,
              property: propertyPayload,
            })
          );
        } else {
          promises.push(editProperty(context.listingId, propertyPayload));
        }
      }

      if (promises.length === 0) {
        throw new Error("No changes detected");
      }

      await Promise.all(promises);
    },
    {
      async onSuccess() {
        await refreshListingCaches();
        showToast({
          type: ToastType.SUCCESS,
          message: "Listing Updated Successfully",
          toastId: "successlistingupdate",
          options: { position: "bottom-left", autoClose: 3000 },
        });
        setOpen(false);
      },
      onError(err: unknown) {
        const message =
          err instanceof Error && err.message === "No changes detected"
            ? "No changes detected"
            : "Failed To Update Listing";
        showToast({
          type: ToastType.ERROR,
          message,
          toastId: "errorlistingupdate",
          options: { position: "bottom-left", autoClose: 3000 },
        });
        // Critical: clear the mutation's error state so a retry actually
        // fires the network call instead of being stuck on the previous
        // error and never re-hitting the backend.
        mutation.reset();
      },
    }
  );

  const handleConfirm = form.handleSubmit(
    (data) => mutation.mutate(data),
    (errors) => {
      // Surface validation failures so the Save click isn't silently a no-op.
      const firstError = Object.values(errors)[0] as { message?: string } | undefined;
      showToast({
        type: ToastType.ERROR,
        message: firstError?.message ?? "Please fix the highlighted fields",
        toastId: "editlistingvalidation",
        options: { position: "bottom-left", autoClose: 3000 },
      });
    }
  );
  const handleClose = () => setOpen(false);

  const formBody = (
    <FormProvider {...form}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <PriceFields askingPrice={listing.unitPrice} />
        {isPersonalLead && (
          <>
            <Divider flexItem sx={{ borderColor: "divider" }} />
            <UnitDetailsFields />
          </>
        )}
      </Box>
    </FormProvider>
  );

  if (isMobile) {
    return (
      <CommonBottomSheet
        isOpen={open}
        onClose={handleClose}
        snapPoints={[0.9, 0.6]}
      >
        <Stack sx={{ height: "100%" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="body_bg_med">Edit Listing</Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
            {formBody}
          </Box>
          <Box sx={{ position: "sticky", bottom: 0 }}>
            <ModalFooter
              confirmButtonText="Save changes"
              cancelButtonText="Cancel"
              onConfirm={handleConfirm}
              onCancel={handleClose}
              isLoading={mutation.isLoading}
            />
          </Box>
        </Stack>
      </CommonBottomSheet>
    );
  }

  return (
    <EditFieldModal
      open={open}
      setOpen={setOpen}
      handleConfirm={handleConfirm}
      confirmText="Save changes"
      width={isPersonalLead ? 520 : 440}
      header="Edit Listing"
      isLoading={mutation.isLoading}
      disabledConfirmButton={mutation.isLoading}
      gap={2.5}
    >
      {formBody}
    </EditFieldModal>
  );
};

export default EditListingModal;
